import { supabase } from "@/lib/supabase";

/**
 * Dashboard KPI helpers. All math runs server-side via the SECURITY DEFINER
 * RPCs in migration 0013 — that's how non-admins are prevented from peeking
 * at other users' numbers. The client just renders what comes back.
 *
 * Status counts come from `lead_status_history` rather than the current
 * `leads.status` so the "how many did we close today" mental model is honored
 * — a lead flipped from closed to follow-up still counts as "closed today"
 * because the closure event happened in the window.
 *
 * Migration 0013 also ships a `leads_by_status_change` RPC for exact
 * per-event drill-down. The dashboard click-through doesn't use it today —
 * we navigate to /leads with URL params and the list filters approximately
 * via leads.status_changed_at. The RPC stays available as a documented
 * primitive; add a wrapper here when an "exact drill-down" surface needs it.
 */

export interface DashboardKpis {
  from: string;
  to: string;
  user: string | null;
  closed: number;
  dnc: number;
  no_answer: number;
  follow_up: number;
  routes_in_progress: number;
  pool_routes: number;
}

export interface FetchKpisInput {
  /** ISO date string (inclusive lower bound on lead_status_history.changed_at). */
  from?: string | null;
  /** ISO date string (exclusive upper bound). */
  to?: string | null;
  /**
   * Whose numbers to fetch:
   *   - undefined / null → team-wide (admins only — non-admins get themselves
   *     regardless, enforced server-side)
   *   - a user uuid → that specific user's numbers (admins only)
   */
  user?: string | null;
}

export async function fetchDashboardKpis(input: FetchKpisInput = {}): Promise<DashboardKpis> {
  const { data, error } = await supabase.rpc("dashboard_kpis", {
    p_from: input.from ?? null,
    p_to: input.to ?? null,
    p_user: input.user ?? null,
  });
  if (error) throw error;
  return data as DashboardKpis;
}
